import React, { useState, useRef, useEffect } from "react";
import { IoChevronDown, IoMenu, IoClose } from "react-icons/io5";
import { useNavigate, useLocation } from "react-router-dom";

// ✅ Ensure this path is correct for your local logo
import logo from "../Components/Mounilogo.png";

const Navbar = ({ onScrollTo }) => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const wrapperRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(null);
        setMobileOpen(false);
        setMobileSubMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleScroll = (key) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: key } });
    } else {
      onScrollTo[key]?.();
    }
  };

  /**
   * ✅ NEW Logic: Pass specific filter to AllCertifications page
   */
  const handleDropdownAction = (vendorName) => {
    setDropdownOpen(null);
    setMobileOpen(false);
    
    // Set 'All' if the special button is clicked, otherwise pass the vendor name
    const filterValue = vendorName === "View All Certs..." ? "All" : vendorName;
    
    // Navigate and pass state so the AllCertifications page knows what to filter
    navigate("/all-certifications", { state: { filter: filterValue } });
  };

  const menuItems = [
    { label: "Home", scrollTo: "home" },
    { label: "Training", scrollTo: "courses" },
    {
      label: "Certifications",
      scrollTo: "certifications",
      dropdown: ["AWS", "Microsoft", "CompTIA", "Google", "Cisco", "View All Certs..."],
    },
    { label: "Need Training?", scrollTo: "needCourses" },
    { label: "Testimonials", scrollTo: "testimonials" },
  ];

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-[100] bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" 
      ref={wrapperRef}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
        
        {/* ✅ Logo Section */}
        <div
          className="flex items-center gap-3 cursor-pointer group"
          onClick={() => handleScroll("home")}
        >
          <div className="p-1.5 bg-purple-50 rounded-xl group-hover:bg-purple-100 transition-all duration-300">
            <img src={logo} alt="Mouni IT Logo" className="w-9 h-9 object-contain" />
          </div>
          <span className="font-black text-xl tracking-tight text-gray-900">
            Mouni <span className="text-[#a300ff]">IT Services</span>
          </span>
        </div>

        {/* ✅ Desktop Menu (Hover Enabled) */}
        <div className="hidden lg:flex items-center gap-1 text-gray-700 font-semibold">
          {menuItems.map((item) => (
            <div 
              key={item.label} 
              className="relative group px-3 py-2"
              onMouseEnter={() => item.dropdown && setDropdownOpen(item.label)}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <button
                onClick={() => handleScroll(item.scrollTo)}
                className={`flex items-center gap-1 transition-colors duration-200 ${
                  dropdownOpen === item.label ? "text-[#a300ff]" : "hover:text-[#a300ff]"
                }`}
              >
                {item.label}
                {item.dropdown && (
                  <IoChevronDown 
                    size={14} 
                    className={`transition-transform duration-300 ${dropdownOpen === item.label ? "rotate-180" : ""}`} 
                  />
                )}
              </button>

              {/* ✅ Desktop Dropdown Box */}
              {item.dropdown && (
                <div className={`absolute top-full left-0 pt-2 transition-all duration-300 transform origin-top ${
                  dropdownOpen === item.label ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                }`}>
                  <div className="bg-white shadow-2xl rounded-2xl border border-gray-100 py-3 w-60 overflow-hidden">
                    {item.dropdown.map((sub) => (
                      <button
                        key={sub}
                        onClick={() => handleDropdownAction(sub)}
                        className={`w-full text-left px-5 py-3 text-sm font-medium transition-all ${
                          sub === "View All Certs..." 
                            ? "text-[#a300ff] font-bold border-t border-gray-50 mt-2 pt-4 hover:bg-purple-50" 
                            : "text-gray-600 hover:bg-gray-50 hover:text-[#a300ff] hover:pl-7"
                        }`}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ✅ Desktop Call-to-Action */}
        <div className="hidden lg:block">
          <button 
            onClick={() => handleScroll("needCourses")}
            className="bg-[#a300ff] text-white font-bold px-7 py-3 rounded-xl hover:shadow-xl hover:shadow-purple-200 transition-all active:scale-95"
          >
            Reach Us ⚡
          </button>
        </div>

        {/* ✅ Mobile Menu Toggle */}
        <div className="lg:hidden">
          <button 
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2.5 bg-gray-50 rounded-xl text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {mobileOpen ? <IoClose size={26} /> : <IoMenu size={26} />}
          </button>
        </div>
      </div>

      {/* ✅ Mobile Menu Overlay */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-100 transition-all duration-500 ease-in-out overflow-hidden ${
        mobileOpen ? "max-h-[100vh] opacity-100 shadow-2xl" : "max-h-0 opacity-0 pointer-events-none"
      }`}>
        <div className="px-6 py-10 space-y-6">
          {menuItems.map((item) => (
            <div key={item.label} className="border-b border-gray-50 pb-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleScroll(item.scrollTo)}
                  className="text-2xl font-black text-gray-900"
                >
                  {item.label}
                </button>
                {item.dropdown && (
                  <button 
                    onClick={() => setMobileSubMenu(mobileSubMenu === item.label ? null : item.label)}
                    className={`p-2 rounded-lg transition-colors ${
                      mobileSubMenu === item.label ? "bg-purple-600 text-white" : "bg-purple-50 text-[#a300ff]"
                    }`}
                  >
                    <IoChevronDown className={`transition-transform duration-300 ${mobileSubMenu === item.label ? "rotate-180" : ""}`} />
                  </button>
                )}
              </div>
              
              {/* Mobile Sub-Menu Grid */}
              {item.dropdown && mobileSubMenu === item.label && (
                <div className="grid grid-cols-2 gap-3 mt-5 animate-fadeIn">
                  {item.dropdown.map((sub) => (
                    <button
                      key={sub}
                      onClick={() => handleDropdownAction(sub)}
                      className={`text-left p-4 rounded-2xl text-sm font-bold transition-all active:scale-95 ${
                        sub === "View All Certs..." 
                        ? "bg-[#a300ff] text-white col-span-2 text-center py-4 shadow-lg" 
                        : "bg-gray-50 text-gray-700 border border-gray-100"
                      }`}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          <button 
             onClick={() => handleScroll("needCourses")}
             className="w-full mt-4 bg-[#a300ff] text-white font-black py-5 rounded-2xl shadow-xl active:scale-95 transition-transform"
          >
            Reach Us ⚡
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;